import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Form, { FormField } from "src/components/ui/Form";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";
import { toast } from "react-toastify";
import { transactionStatuses, transactionTypes } from "src/constants/common";

const TransactionForm: React.FC = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { createTransaction, editTransaction, transactionDetails } =
    useAppSelector((state) => state.transaction);
  const [paymentMethod, setPaymentMethod] = useState<string>(
    transactionDetails.data?.paymentMethod || "cash"
  );

  useEffect(() => {
    setPaymentMethod(transactionDetails.data?.paymentMethod || "cash");
  }, [transactionDetails.data?.paymentMethod]);

  useEffect(() => {
    if (transactionId) {
      dispatch(transactionActions.getTransactionDetailsRequest(transactionId));
    } else {
      dispatch(transactionActions.resetTransactionDetails());
    }
  }, [dispatch, transactionId]);

  useEffect(() => {
    if (transactionDetails.error) {
      toast.error("Failed to fetch transaction details");
    }
  }, [transactionDetails.error]);

  const fields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        label: "Transaction Type",
        name: "transactionType",
        type: "dropdown",
        options: transactionTypes.map((type) => ({
          label: type,
          value: type,
        })),
        defaultValue: "payment",
        validations: { required: true },
      },
      {
        label: "Payment Method",
        name: "paymentMethod",
        type: "dropdown",
        options: [
          { label: "Cash", value: "cash" },
          { label: "Online", value: "online" },
        ],
        defaultValue: "cash",
        validations: { required: true },
        onChange: (value) => setPaymentMethod(value),
      },
      {
        label: "Amount",
        name: "amount",
        type: "number",
        placeholder: "Enter Amount",
        validations: { required: true },
      },
      {
        label: "Status",
        name: "status",
        type: "dropdown",
        options: transactionStatuses.map((status) => ({
          label: status,
          value: status,
        })),
        defaultValue: "created",
        validations: { required: true },
      },
      {
        label: "Remarks",
        name: "remarks",
        type: "textarea",
        placeholder: "Add remarks (optional)",
      },
      {
        label: "Data (optional)",
        name: "data",
        type: "textarea",
        placeholder: "Add JSON string (optional)",
      },
    ];

    if (paymentMethod === "online") {
      baseFields.splice(2, 0, {
        label: "Payment Mode",
        name: "paymentMode",
        type: "dropdown",
        options: [
          { label: "PhonePe", value: "phonepay" },
          { label: "GPay", value: "gpay" },
          { label: "Paytm", value: "paytm" },
          { label: "Other", value: "other" },
        ],
        validations: { required: true },
      });
      baseFields.splice(3, 0, {
        label: "Reference Id",
        name: "referenceId",
        type: "text",
        placeholder: "Enter Reference Id",
        validations: { required: true },
      });
    }

    return baseFields;
  }, [paymentMethod]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (createTransaction.data) {
      toast.success("Transaction recorded successfully");
      handleClose();
    } else if (createTransaction.error) {
      toast.error(`Error: ${createTransaction.error}`);
    }
  }, [createTransaction]);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editTransaction.data) {
      toast.success("Transaction updated successfully");
      handleClose();
    } else if (editTransaction.error) {
      toast.error(`Error: ${editTransaction.error}`);
    }
  }, [editTransaction]);

  const handleClose = () => {
    dispatch(transactionActions.resetCreateTransaction());
    dispatch(transactionActions.resetEditTransaction());
    navigate("/admin/transaction/list");
  };

  const handleSubmit = (data: Record<string, any>) => {
    console.log("handleSubmit", data);
    const payload = { ...data };
    if (payload.paymentMethod !== "online") {
      delete payload.paymentMode;
      delete payload.referenceId;
    }

    if (transactionDetails.data?._id) {
      dispatch(
        transactionActions.editTransactionRequest({
          id: transactionDetails.data._id,
          data: payload,
        })
      );
    } else {
      dispatch(transactionActions.createTransactionRequest(payload));
    }
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: transactionId
          ? "Edit Transaction | Invyntra"
          : "Create Transaction | Invyntra",
        description: "Create or edit transaction details.",
        keywords: "transactions, details, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={transactionId ? "Edit Transaction" : "Create Transaction"}
          showBack={!!transactionId}
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Transactions", path: "/admin/transaction/list" },
            {
              label: transactionId ? "Edit Transaction" : "Create Transaction",
              path: "",
            },
          ]}
        />
        <Card>
          <Form
            initialState={transactionDetails.data as any}
            fields={fields}
            onSubmit={handleSubmit}
            onCancel={() => navigate("/admin/transaction/list")}
            loading={
              createTransaction.loading ||
              editTransaction.loading ||
              transactionDetails.loading
            }
          />
        </Card>
      </div>
    </Layout>
  );
};

export default TransactionForm;
